import { useEffect } from "react";

/**
 * Inject a JSON-LD <script type="application/ld+json"> into <head>.
 * Removes itself on unmount so per-page schemas don't leak between routes.
 */
export function SchemaJsonLd({ data, id }) {
  useEffect(() => {
    // Jun 2026 — a null/empty schema must emit NOTHING. Previously this
    // wrote the literal string "null" into a JSON-LD script tag.
    if (!data) return undefined;
    const scriptId = id || `jsonld-${Math.random().toString(36).slice(2)}`;
    let el = document.getElementById(scriptId);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = scriptId;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => {
      const node = document.getElementById(scriptId);
      if (node) node.remove();
    };
  }, [data, id]);
  return null;
}

// --- Schema builders ---


const ORG_BASE = {
  "@type": "LocalBusiness",
  "@id": "https://awarewatertesting.com.au/#org",
  name: "Aware Water Testing",
  url: "https://awarewatertesting.com.au",
  telephone: "+61753273473",
  email: "lab@awarewatertesting.com.au",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "1/37 Gateway Drive",
    addressLocality: "Noosaville",
    addressRegion: "QLD",
    postalCode: "4566",
    addressCountry: "AU",
  },
  areaServed: [
    "Australia",
    "Sunshine Coast, QLD",
    "Noosa", "Noosaville", "Tewantin", "Cooroy", "Pomona", "Eumundi",
    "Doonan", "Yandina", "Coolum", "Buderim", "Maroochydore",
    "Nambour", "Maleny", "Montville", "Caloundra",
  ],
  description: "Independent Australian water testing laboratory based in Noosaville, Queensland. Plain-English reports and expert recommendations on rainwater, bore water and drinking water — supporting customers Australia-wide.",
};

/**
 * DEPRECATED (Jun 2026) — returns null on purpose.
 *
 * `useSEO`'s baseline already emits the authoritative LocalBusiness on
 * every public page (see buildLocalBusinessLd in lib/useSEO.js). These
 * legacy builders were ALSO rendered by 9 page templates, so the
 * homepage published two Organization and two LocalBusiness entities
 * with inconsistent urls. Conflicting descriptions of the same business
 * make it harder for Google to consolidate the entity — which is
 * exactly the problem behind the old brand persisting in AI results.
 *
 * Kept as no-ops so the ~9 existing call sites stay valid. Add business
 * schema in ONE place only: buildLocalBusinessLd().
 */
export function localBusinessSchema() {
  return null;
}

/**
 * Top-level Organization entity for the brand. Emitted on the homepage so
 * Google can disambiguate the publisher (sitelinks signal). Uses a distinct
 * @id from LocalBusiness so the two entities can coexist without conflict.
 *
 * Intentionally conservative: NO accreditation, NATA, certification, or
 * "official" wording. Just verifiable public facts.
 */
export function organizationSchema() {
  // DEPRECATED (Jun 2026) — no-op. See localBusinessSchema() above:
  // useSEO's buildOrganizationLd() is the single authoritative
  // Organization entity. Emitting a second one here gave the homepage
  // two conflicting Organization blocks.
  return null;
}

/**
 * WebSite entity for the homepage. SearchAction is intentionally omitted —
 * the site has no real internal search route; emitting a fake one would be
 * structured-data spam (and would be penalised by Google).
 */
export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://awarewatertesting.com.au/#website",
    url: "https://awarewatertesting.com.au",
    name: "Aware Water Testing",
    publisher: { "@id": "https://awarewatertesting.com.au/#organization" },
  };
}

export function serviceSchema(serviceName, description, suburb) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    serviceType: "Water Testing",
    description,
    areaServed: suburb ? { "@type": "City", name: suburb } : "Australia",
    provider: ORG_BASE,
  };
}

export function productSchema({ name, price, description, ratingValue = "5", reviewCount = "47" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    brand: { "@type": "Brand", name: "Aware Water Testing" },
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: String(price),
      availability: "https://schema.org/InStock",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue,
      reviewCount,
    },
  };
}

export function faqSchema(qas) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: qas.map((qa) => ({
      "@type": "Question",
      name: qa.q,
      acceptedAnswer: { "@type": "Answer", text: qa.a },
    })),
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
